/**
 * Capability-based permissions — mirrors src/api/permissions.py.
 *
 * Editor and operator are at the SAME level with cross-cutting capabilities:
 *   editor  → queue write + tickets read-only
 *   operator → tickets write + queue read-only
 */

const CAN_WORK_TICKETS = new Set(["platform_admin", "admin", "operator"]);
const CAN_MANAGE_QUEUE = new Set(["platform_admin", "admin", "editor"]);
// Visibilità per-modulo sulla coda output (Item 1, 2026-07-14) — mirrors
// CAN_VIEW_RDI_QUEUE/CAN_VIEW_DDT_QUEUE in src/api/permissions.py. Sostituisce
// la vecchia CAN_VIEW_QUEUE unica (che includeva anche "viewer", qui rimosso
// deliberatamente: la coda operativa è per chi lavora ticket/pratiche o la
// gestisce, non per ruoli di sola consultazione).
const CAN_VIEW_RDI_QUEUE = new Set(["platform_admin", "admin", "editor", "operator"]);
const CAN_VIEW_DDT_QUEUE = new Set(["platform_admin", "admin", "editor", "ddt_operator"]);
const CAN_CONFIGURE = new Set(["platform_admin", "admin"]);
const CAN_ADMIN = new Set(["platform_admin"]);
const CAN_MANAGE_USERS = new Set(["platform_admin", "admin"]);
const CAN_USE_DELEGHE = new Set(["platform_admin", "admin", "delega_operator"]);

function hasAny(roles: string[], allowed: Set<string>): boolean {
  return roles.some((r) => allowed.has(r));
}

export function canWorkTickets(roles: string[]): boolean {
  return hasAny(roles, CAN_WORK_TICKETS);
}

export function canManageQueue(roles: string[]): boolean {
  return hasAny(roles, CAN_MANAGE_QUEUE);
}

export function canViewRdiQueue(roles: string[]): boolean {
  return hasAny(roles, CAN_VIEW_RDI_QUEUE);
}

export function canViewDdtQueue(roles: string[]): boolean {
  return hasAny(roles, CAN_VIEW_DDT_QUEUE);
}

export function canConfigure(roles: string[]): boolean {
  return hasAny(roles, CAN_CONFIGURE);
}

export function canAdmin(roles: string[]): boolean {
  return hasAny(roles, CAN_ADMIN);
}

export function canManageUsers(roles: string[]): boolean {
  return hasAny(roles, CAN_MANAGE_USERS);
}

export function canUseDeleghe(roles: string[]): boolean {
  return hasAny(roles, CAN_USE_DELEGHE);
}

export function canUseBsc(roles: string[]): boolean {
  return roles.includes("platform_admin") ||
         roles.includes("admin") ||
         roles.includes("bsc_operator");
}

export function canUseDdt(roles: string[]): boolean {
  return roles.includes("platform_admin") ||
         roles.includes("admin") ||
         roles.includes("ddt_operator");
}

/** L'agente conversazionale e' configurazione, non lavorazione di coda: chi ne
 * apre l'interfaccia decide come l'agente parla ai clienti. Per questo NON
 * include `operator` — le pratiche RDI che l'agente genera restano nella coda
 * RDI, dove l'operatore le lavora con i permessi che ha gia'.
 * Lato server la stessa regola e' in `src/api/permissions.py`
 * (`CAN_USE_CONVERSAZIONI`): questa funzione nasconde un link, non protegge. */
export function canUseConversazioni(roles: string[]): boolean {
  return roles.includes("platform_admin") || roles.includes("admin");
}

export function canUseRinascente(roles: string[]): boolean {
  return roles.includes("platform_admin") ||
         roles.includes("admin") ||
         roles.includes("rinascente_operator");
}

export const ROLE_LABELS: Record<string, string> = {
  platform_admin: "Platform Admin",
  admin: "Amministratore",
  editor: "Editor",
  operator: "Operatore",
  viewer: "Viewer",
  delega_operator: "Operatore Deleghe",
  bsc_operator: "Operatore BSC",
  ddt_operator: "Operatore DDT",
  rinascente_operator: "Operatore Rinascente",
};
